import type { StaffCommandRequest } from "@/features/benefit-workspace/benefitEditorSlice"
import { extractApiError } from "@/lib/api-errors"

export class StaffCommandNotSentError extends Error {
  constructor(readonly code: "command-busy" | "authentication-unavailable" | "editor-unavailable") {
    super(code === "authentication-unavailable" ? "Authentication is unavailable. The request was not sent."
      : "Finish or review the active operation first. The request was not sent.")
    this.name = "StaffCommandNotSentError"
  }
}
/**
 * A 409 is not always a concurrent edit: the server also rejects identity corrections awaiting
 * review, duplicate identifiers and protected workflow states. Show its reason when it gave one.
 */
function conflictMessage(serverReason: string) {
  const retained = "Your draft and original token are retained for review."
  return serverReason ? `${serverReason.trim()} ${retained}` : `The saved application changed. ${retained}`
}
export function commandFailure(error: unknown) {
  const failure = error as { status?: number | string; originalStatus?: number; code?: string; data?: { code?: string; errors?: Record<string, string[]> }; fieldErrors?: Record<string, string[]>; body?: { errors?: Record<string, string[]> }; message?: string }
  const status = typeof failure?.status === "number" ? failure.status : failure?.originalStatus
  const code = failure?.code ?? failure?.data?.code
  const outcome = error instanceof StaffCommandNotSentError || ["access-check-unavailable", "read-reconciliation-unavailable", "authentication-unavailable", "cancelled-before-send", "scope-unavailable-before-send"].includes(code ?? "") ? "notSent"
    : status === 401 || status === 403 || status === 404 ? "denied"
    : status === 409 ? "conflict" : status && status >= 400 && status < 500 && status !== 408 ? "validation" : "unknown"
  return { outcome: outcome as "notSent" | "denied" | "conflict" | "validation" | "unknown",
    message: outcome === "unknown" ? "The request may have committed. Its outcome is unknown; it will not be sent again automatically."
      : outcome === "conflict" ? conflictMessage(extractApiError(failure?.body ?? failure?.data))
      : failure?.message ?? "The request was not accepted.",
    fieldErrors: failure?.fieldErrors ?? failure?.body?.errors ?? failure?.data?.errors ?? {} }
}

export type StaffCommandOwner = <T>(request: StaffCommandRequest, send: (request: StaffCommandRequest) => Promise<T>) => Promise<T>
interface Owner { paths: readonly string[]; run: StaffCommandOwner; remove(): void }
const owners = new Set<Owner>()
export function registerStaffCommandOwner(paths: readonly string[], run: StaffCommandOwner,
  lifetime?: { retain(): boolean; subscribe(listener: () => void): () => void }) {
  // Remount replaces the runtime owner, never the Redux attempt or its gate.
  for (const owner of owners) if (JSON.stringify(owner.paths) === JSON.stringify(paths)) owner.remove()
  let unsubscribe: (() => void) | undefined
  const entry: Owner = { paths, run, remove: () => { owners.delete(entry); unsubscribe?.(); unsubscribe = undefined } }
  owners.add(entry)
  return () => {
    if (!owners.has(entry)) return
    if (!lifetime?.retain()) entry.remove()
    else {
      unsubscribe = lifetime.subscribe(() => { if (!lifetime.retain()) entry.remove() })
      if (!lifetime.retain()) entry.remove()
    }
  }
}
function ownerFor(path: string) {
  const pathname = new URL(path, "https://staff.invalid").pathname
  return [...owners].find(item => item.paths.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)))
}
export const isStaffCommandManaged = (path: string) => !!ownerFor(path)
/** Endpoint-specific builders remain in their features; seal their exact JSON at the send boundary. */
export function runStaffCommand<T>(request: StaffCommandRequest, send: (request: StaffCommandRequest) => Promise<T>): Promise<T> {
  const owner = ownerFor(request.path)
  return owner ? owner.run(Object.freeze({ ...request }), send) : send(request)
}
